import React, { useState, useCallback, useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  OutlinedInput,
  Snackbar,
  SnackbarCloseReason,
  Typography,
} from "@mui/material";
import { signUp, signIn } from "aws-amplify/auth";
import { ExpandMore } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import mixpanel from "mixpanel-browser";

import { User, UserError } from "types/user";
import { useLookup } from "api/lookupService";
import Layout from "components/layout/layout";
import { Address } from "components/user-information/address";
import { ContactInformation } from "components/user-information/contact-information";
import { PersonalInformation } from "components/user-information/personal-information";
import { USER, USER_ERROR } from "constants/user";
import { BackToPreviousButton } from "components/shared/back-to-previous-button";
import { EmailConfirm } from "./email-confirm";
import { useAddUserToFundMutation } from "hooks/use-services/user.service";
import { errorAlert } from "style/color";
import { useIsUserInviteExpiredQuery } from "hooks/use-services/fund-account-invitations.service";
import { INVITE_REGISTRATION_IN_PROGRESS_KEY } from "providers/userProvider";

// Types
interface SignUpParameters {
  username: string;
  password: string;
}

interface PasswordState {
  password: string;
  confirmPassword: string;
  error: boolean;
  confirmError: boolean;
  validationError: string;
}

interface SnackbarState {
  open: boolean;
  error: boolean;
}

interface AgreementState {
  acceptAgreement: boolean;
  acceptPolicy: boolean;
}

// Constants
const PASSWORD_VALIDATION_RULES = {
  minLength: 8,
  hasUpperCase: /[A-Z]/,
  hasLowerCase: /[a-z]/,
  hasNumber: /\d/,
  hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/,
};

const VALIDATION_MESSAGES = {
  minLength: (min: number) => `Password must be at least ${min} characters long.`,
  upperCase: "Password must contain at least one uppercase letter.",
  lowerCase: "Password must contain at least one lowercase letter.",
  number: "Password must contain at least one number.",
  specialChar: "Password must contain at least one special character.",
  required: "Field is required",
  noMatch: "Passwords do not match",
  existingAccount: "There is already an account with this email",
};

export const SignUpByInvite = () => {
  const { id, email, fund, role } = useParams();
  const navigate = useNavigate();

  // State management
  const [target, setTarget] = useState<any>(null);
  const [scroll, setScroll] = useState(false);
  const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, error: false });
  const [user, setUser] = useState<User>(USER);
  const [error, setError] = useState<UserError>(USER_ERROR);
  const [titles, setTitles] = useState([]);
  const [agreements, setAgreements] = useState<AgreementState>({
    acceptAgreement: false,
    acceptPolicy: false,
  });
  const [passwordState, setPasswordState] = useState<PasswordState>({
    password: "",
    confirmPassword: "",
    error: false,
    confirmError: false,
    validationError: "",
  });

  // Hooks
  const { data: isInviteExpired } = useIsUserInviteExpiredQuery(parseInt(id || "0"));
  const { mutateAsync: addUserToFund } = useAddUserToFundMutation();
  const setLookupData = useCallback((data: any) => setTitles(data.titles), []);
  useLookup(setLookupData);

  // Utility functions
  const validatePassword = useCallback((password: string): string => {
    const { minLength, hasUpperCase, hasLowerCase, hasNumber, hasSpecialChar } = PASSWORD_VALIDATION_RULES;

    if (password.length < minLength) return VALIDATION_MESSAGES.minLength(minLength);
    if (!hasUpperCase.test(password)) return VALIDATION_MESSAGES.upperCase;
    if (!hasLowerCase.test(password)) return VALIDATION_MESSAGES.lowerCase;
    if (!hasNumber.test(password)) return VALIDATION_MESSAGES.number;
    if (!hasSpecialChar.test(password)) return VALIDATION_MESSAGES.specialChar;

    return "";
  }, []);

  const trackUserSignup = useCallback((user: User, email: string, fund: string) => {
    if (mixpanel && typeof mixpanel.track === "function") {
      try {
        mixpanel.track("UserSignedUpWithInvitation", {
          user: `${user.firstName} ${user.lastName}`,
          email: email || "",
          fundAccount: fund || "",
        });
      } catch (trackError) {
        console.error("Error tracking user signup:", trackError);
      }
    }
  }, []);

  // Event handlers
  const handleUserChange = useCallback((key: string, value: any) => {
    setScroll(false);
    setError(prev => ({ ...prev, [key]: false }));
    setUser(prev => ({ ...prev, [key]: value }));
  }, []);

  const handlePasswordChange = useCallback((field: 'password' | 'confirmPassword', value: string) => {
    setPasswordState(prev => ({
      ...prev,
      [field]: value,
      error: field === 'password' ? false : prev.error,
      confirmError: field === 'confirmPassword' ? false : prev.confirmError,
      validationError: field === 'password' ? validatePassword(value) : prev.validationError,
    }));
  }, [validatePassword]);

  const handleAgreementChange = useCallback((field: keyof AgreementState, checked: boolean) => {
    setAgreements(prev => ({ ...prev, [field]: checked }));
  }, []);

  const handleInvalid = useCallback((event: any) => {
    if (!scroll) {
      event.target.scrollIntoView({ behavior: "smooth", block: "center" });
      setScroll(true);
    } else {
      event.preventDefault();
    }
    setError(prev => ({ ...prev, [event.target.name]: true }));
  }, [scroll]);

  const handleFutureDateError = useCallback((target: any, error: any) => {
    setTarget(error ? target : null);
  }, []);

  const handleSnackbarClose = useCallback((
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") return;
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const handleSignUp = useCallback(async ({ username, password }: SignUpParameters) => {
    try {
      const { userId } = await signUp({ username, password });
      handleUserChange("sub", userId);
      // The backend user is created after email confirmation and sign-in
      // (see handleAddUser), because accepting an invite requires a token.
      setEmailConfirmOpen(true);
    } catch (error: any) {
      if (error.name === "UsernameExistsException") {
        setSnackbar({ open: true, error: true });
      }
    }
  }, [handleUserChange]);

  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      await handleSignUp({ username: email!, password: passwordState.password });
    }
  }, [target, handleSignUp, email, passwordState.password]);

  const handleAddUser = useCallback(async () => {
    setEmailConfirmOpen(false);
    sessionStorage.setItem(INVITE_REGISTRATION_IN_PROGRESS_KEY, "true");

    try {
      // Sign in the user after email confirmation
      await signIn({ username: email!, password: passwordState.password });
    } catch (error) {
      sessionStorage.removeItem(INVITE_REGISTRATION_IN_PROGRESS_KEY);
      navigate('/signIn', {
        replace: true,
      });
      // Handle sign-in error - maybe show a snackbar or redirect to login
      setSnackbar({
        open: true,
        error: true
      });
      return;
    }

    try {
      // Now authenticated: the backend takes the user's identity from the
      // token and checks the invitation was sent to this email.
      await addUserToFund({ ...user, inviteId: parseInt(id || "0") });
      try {
        trackUserSignup(user, email!, fund!);
      } catch (error) {
        console.error("Error tracking user signup:", error);
      }
      // Reload so the user state (and their new fund) is loaded
      window.location.reload();
    } catch (error) {
      console.error("Error adding user to fund:", error);
      setSnackbar({
        open: true,
        error: true
      });
    } finally {
      sessionStorage.removeItem(INVITE_REGISTRATION_IN_PROGRESS_KEY);
    }
  }, [email, passwordState.password, navigate, addUserToFund, user, id, trackUserSignup, fund]);
  // Computed values
  const isFormValid = useMemo(() => {
    return agreements.acceptAgreement &&
      agreements.acceptPolicy &&
      !isInviteExpired &&
      passwordState.password &&
      passwordState.confirmPassword &&
      passwordState.password === passwordState.confirmPassword &&
      !passwordState.validationError;
  }, [agreements, isInviteExpired, passwordState]);

  const passwordMismatch = useMemo(() => {
    return passwordState.confirmPassword && passwordState.confirmPassword !== passwordState.password;
  }, [passwordState.confirmPassword, passwordState.password]);

  return (
    <Layout>
      {() => (
        <>
          {isInviteExpired && (
            <Alert
              severity="error"
              variant="filled"
              sx={{ width: "100%", backgroundColor: errorAlert[300] }}
            >
              This invitation has been expired. Please{" "}
              <Link
                target="_blank"
                href="mailto:admin@netafoundation.org"
                underline="always"
                color="inherit"
                sx={{ "&:hover": { cursor: "pointer" } }}
              >
                contact us
              </Link>{" "}
              for further assistance.
            </Alert>
          )}

          <Container>
            <Box sx={{ my: 4, width: "100%" }}>
              <Container sx={{ my: 8, width: "100%" }}>
                <Grid sx={{ mb: 5 }} container spacing={2}>
                  <Snackbar
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    autoHideDuration={3000}
                    open={snackbar.open}
                    onClose={handleSnackbarClose}
                  >
                    <Alert
                      onClose={handleSnackbarClose}
                      severity="error"
                      variant="filled"
                      sx={{ width: "100%", backgroundColor: errorAlert[300] }}
                    >
                      {VALIDATION_MESSAGES.existingAccount}
                    </Alert>
                  </Snackbar>

                  <Grid item xs={12} sm={8}>
                    <BackToPreviousButton />
                  </Grid>

                  <Grid item xs={12} sm={10}>
                    <Typography variant="h3">
                      Please sign up to become {role === "VIEWER" ? "a" : "an"} {role} to {fund}
                    </Typography>
                    <Typography variant="bodyCopyRegular">
                      Please complete all mandatory fields (*).
                    </Typography>
                  </Grid>
                </Grid>

                <EmailConfirm
                  open={emailConfirmOpen}
                  email={email!}
                  handleClose={handleAddUser}
                />

                <Box component="form" autoComplete="off" onSubmit={handleSubmit}>
                  <Accordion defaultExpanded>
                    <AccordionSummary
                      tabIndex={-1}
                      expandIcon={<ExpandMore />}
                      aria-controls="login"
                      id="login"
                    >
                      <Typography sx={{ color: "neutral.main" }} variant="subtitle1">
                        Login Information
                      </Typography>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Grid sx={{ mt: 2 }} container spacing={2}>
                        <Grid item xs={12} sm={8}>
                          <FormControl fullWidth>
                            <InputLabel shrink={false}>Email*</InputLabel>
                            <OutlinedInput
                              name="email"
                              value={email!}
                              type="text"
                              required
                              disabled
                            />
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={8}>
                          <FormControl error={passwordState.error} fullWidth>
                            <InputLabel shrink={false}>Password*</InputLabel>
                            <OutlinedInput
                              value={passwordState.password}
                              name="password"
                              type="password"
                              onChange={(e) => handlePasswordChange('password', e.target.value)}
                              onInvalid={() => setPasswordState(prev => ({ ...prev, error: true }))}
                              required
                            />
                            {passwordState.error && (
                              <FormHelperText error>
                                {VALIDATION_MESSAGES.required}
                              </FormHelperText>
                            )}
                            {passwordState.validationError && (
                              <FormHelperText error>
                                {passwordState.validationError}
                              </FormHelperText>
                            )}
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={8}>
                          <FormControl error={passwordState.confirmError} fullWidth>
                            <InputLabel shrink={false}>Repeat password*</InputLabel>
                            <OutlinedInput
                              value={passwordState.confirmPassword}
                              name="confirmPassword"
                              type="password"
                              onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                              onInvalid={() => setPasswordState(prev => ({ ...prev, confirmError: true }))}
                              required
                            />
                            {passwordState.confirmError && (
                              <FormHelperText error>
                                {VALIDATION_MESSAGES.required}
                              </FormHelperText>
                            )}
                            {passwordMismatch && (
                              <FormHelperText error>
                                {VALIDATION_MESSAGES.noMatch}
                              </FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  <PersonalInformation
                    titles={titles}
                    user={user}
                    handleChange={handleUserChange}
                    error={error}
                    handleInvalid={handleInvalid}
                    handleFutureDateError={handleFutureDateError}
                  />

                  <Address
                    user={user}
                    handleChange={handleUserChange}
                    error={error}
                    handleInvalid={handleInvalid}
                  />

                  <ContactInformation
                    user={user}
                    handleChange={handleUserChange}
                    error={error}
                    handleInvalid={handleInvalid}
                  />

                  <Grid sx={{ mt: 2 }} container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        sx={{ alignItems: "flex-start" }}
                        control={
                          <Checkbox
                            sx={{ pt: 0 }}
                            color="primary"
                            name="acceptAgreement"
                            checked={agreements.acceptAgreement}
                            onChange={(e) => handleAgreementChange('acceptAgreement', e.target.checked)}
                          />
                        }
                        label={
                          <>
                            <Typography variant="bodyCopyRegular">
                              By ticking the box, you confirm that you agree with{" "}
                            </Typography>
                            <Typography variant="bodyCopyBold">
                              <Link
                                href="/agreement-policies"
                                target="_blank"
                                underline="always"
                                color="inherit"
                                sx={{ mr: 1, "&:hover": { cursor: "pointer" } }}
                              >
                                Neta's Fund agreement policies
                              </Link>
                            </Typography>
                          </>
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        sx={{ alignItems: "flex-start" }}
                        control={
                          <Checkbox
                            sx={{ pt: 0 }}
                            color="primary"
                            name="acceptPolicy"
                            checked={agreements.acceptPolicy}
                            onChange={(e) => handleAgreementChange('acceptPolicy', e.target.checked)}
                          />
                        }
                        label={
                          <>
                            <Typography variant="bodyCopyRegular">
                              By ticking the box, you confirm that you agree with{" "}
                            </Typography>
                            <Typography variant="bodyCopyBold">
                              <Link
                                href="/policy"
                                target="_blank"
                                underline="always"
                                color="inherit"
                                sx={{ mr: 1, "&:hover": { cursor: "pointer" } }}
                              >
                                Neta's privacy policy
                              </Link>
                            </Typography>
                          </>
                        }
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                    <Box sx={{ flex: "1 1 auto" }} />
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      type="submit"
                      disabled={!isFormValid}
                    >
                      Confirm
                    </Button>
                  </Box>
                </Box>
              </Container>
            </Box>
          </Container>
        </>
      )}
    </Layout>
  );
};
